<script setup>
import { ref, onMounted } from 'vue';
import { usePasskeyAuth } from 'shared/composables/usePasskeyAuth';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const { t } = useI18n();

const { isSupported, isLoading, register, listCredentials, removeCredential } =
  usePasskeyAuth();

const credentials = ref([]);
const isRegisterModalOpen = ref(false);
const deviceName = ref('');

const fetchCredentials = async () => {
  credentials.value = await listCredentials();
};

onMounted(() => {
  fetchCredentials();
});

const handleRegister = async () => {
  if (!deviceName.value.trim()) {
    useAlert(t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.DEVICE_NAME_REQUIRED'));
    return;
  }

  const result = await register(deviceName.value.trim());
  if (result && result.success) {
    useAlert(t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.REGISTER_SUCCESS'));
    isRegisterModalOpen.value = false;
    deviceName.value = '';
    await fetchCredentials();
  } else {
    useAlert(t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.REGISTER_ERROR'));
  }
};

const handleDelete = async id => {
  // eslint-disable-next-line no-alert
  const shouldDelete = window.confirm(
    t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.DELETE_CONFIRM')
  );

  if (shouldDelete) {
    const success = await removeCredential(id);
    if (success) {
      useAlert(t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.DELETE_SUCCESS'));
      await fetchCredentials();
    } else {
      useAlert(t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.DELETE_ERROR'));
    }
  }
};

const formatDate = dateStr => {
  if (!dateStr) return t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.NEVER_USED');
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
  <div
    class="bg-n-background rounded-xl p-5 border border-n-slate-4 flex flex-col gap-5 w-full"
  >
    <!-- Se navegador não suportar WebAuthn -->
    <div
      v-if="!isSupported"
      class="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-xl p-4 text-sm text-red-800 dark:text-red-300 flex items-start gap-2.5"
    >
      <Icon
        icon="i-lucide-info"
        class="size-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
      />
      <span>{{
        $t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.NOT_SUPPORTED')
      }}</span>
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Lista de credenciais -->
      <div
        v-if="credentials.length > 0"
        class="flex flex-col border border-n-slate-4 rounded-xl overflow-hidden bg-n-slate-1/50"
      >
        <div
          v-for="cred in credentials"
          :key="cred.id"
          class="flex justify-between items-center p-4 border-b border-n-slate-4 last:border-b-0 hover:bg-n-slate-2/50 transition-colors"
        >
          <div class="flex items-center gap-3">
            <Icon
              icon="i-lucide-key-round"
              class="size-5 text-n-slate-9 flex-shrink-0"
            />
            <div class="flex flex-col">
              <span class="text-sm font-medium text-n-slate-12">{{
                cred.deviceName
              }}</span>
              <span class="text-xs text-n-slate-11">
                {{ $t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.REGISTERED_AT')
                }}{{ formatDate(cred.createdAt) }}
              </span>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            color="ruby"
            faded
            icon="i-lucide-trash-2"
            :label="$t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.DELETE_BUTTON')"
            class="flex-shrink-0"
            @click="handleDelete(cred.id)"
          />
        </div>
      </div>

      <!-- Sem chaves cadastradas (Callout Premium de orientacao) -->
      <div
        v-else
        class="flex flex-col items-center justify-center p-6 border border-dashed border-n-slate-4 rounded-xl bg-n-slate-2/50 text-center gap-2.5"
      >
        <div
          class="p-2.5 bg-n-slate-3 dark:bg-n-slate-4 rounded-full text-n-slate-9"
        >
          <Icon icon="i-lucide-fingerprint" class="size-6" />
        </div>
        <p class="text-sm text-n-slate-11 max-w-sm">
          {{ $t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.NO_KEYS') }}
        </p>
      </div>

      <!-- Ações -->
      <div class="flex justify-start">
        <Button
          type="button"
          :disabled="isLoading"
          :is-loading="isLoading"
          icon="i-lucide-plus"
          @click="isRegisterModalOpen = true"
        >
          <span class="flex items-center gap-1.5">
            {{ $t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.ADD_BUTTON') }}
          </span>
        </Button>
      </div>
    </div>

    <!-- Modal de Registro -->
    <div
      v-if="isRegisterModalOpen"
      class="fixed inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <div
        class="bg-n-background border border-n-slate-4 rounded-2xl max-w-md w-full shadow-2xl p-6 flex flex-col gap-5 transform scale-100 transition-all"
      >
        <div
          class="flex justify-between items-center border-b border-n-slate-4 pb-3"
        >
          <h4
            class="text-base font-semibold text-n-slate-12 flex items-center gap-2"
          >
            <Icon
              icon="i-lucide-shield-check"
              class="size-5 text-emerald-600 dark:text-emerald-400 animate-pulse"
            />
            {{ $t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.MODAL_TITLE') }}
          </h4>
          <button
            class="text-n-slate-10 hover:text-n-slate-12 text-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
            @click="isRegisterModalOpen = false"
          >
            &times;
          </button>
        </div>

        <div class="flex flex-col gap-4">
          <p class="text-sm text-n-slate-11">
            {{ $t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.MODAL_DESC') }}
          </p>

          <div class="flex flex-col gap-1.5">
            <label
              class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
            >
              {{
                $t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.DEVICE_NAME_LABEL')
              }}
            </label>
            <input
              v-model="deviceName"
              type="text"
              :placeholder="
                $t(
                  'PROFILE_SETTINGS.FORM.PASSKEY_SECTION.DEVICE_NAME_PLACEHOLDER'
                )
              "
              class="w-full border border-n-slate-4 rounded-xl px-3.5 py-2.5 bg-n-slate-1 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-n-brand transition-all"
            />
          </div>
        </div>

        <div class="flex justify-end gap-3 border-t border-n-slate-4 pt-4">
          <Button
            type="button"
            variant="outline"
            :label="$t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.CANCEL')"
            @click="isRegisterModalOpen = false"
          />
          <Button
            type="button"
            color-scheme="primary"
            :label="
              $t('PROFILE_SETTINGS.FORM.PASSKEY_SECTION.START_BIOMETRICS')
            "
            @click="handleRegister"
          />
        </div>
      </div>
    </div>
  </div>
</template>
